import React from "react";
import { useTheme } from "react-jss";
import { useSelector } from "react-redux";
import { StoreState } from "../../../models/states/StoreState";

import useStyles from "./NavigationBarRenderer.styles";
import { NavigationType } from "../../../models/enums/NavigationType";
import { ThemeModel } from "../../../models/ThemeModel";
import { ShortcutModel } from "../../../models/ShortcutModel";
import Button from "../../shared/Button";
import useTranslatable from "../../../hooks/useTranslatable";
import useDimensionContext from "../../../hooks/useDimensionContext";
import useBlock from "../../../hooks/useBlock";
import { isFocusable } from "../../../models/utilities/defaultFocusStatusUtilities";
import { getSettingBoolean } from "../../../models/utilities/settingUtilities";
import type { Theme } from "../../../types/Theme";
import ViewResponsesButton from "../../page/ViewResponsesButton";
import { shouldUseModernTheme } from "../../../theme/navigationButtonThemeColors";
import { shouldShowViewResponsesAtNavPosition } from "../../../utils/viewResponsesVisibility";

interface Props {
    surveyTheme: ThemeModel;
    disabled: boolean;
    shortcuts: ShortcutModel[];
    hasPreviousPage: boolean;
    hasNextPage: boolean;
    hideBackButton: boolean;
    onNavigateShortcut(shortcut: ShortcutModel): void;
    onNavigation(type: NavigationType): void;
    position: "top" | "bottom";
    showSecurityQuestion: boolean;
}

const getButtons = (props: Props, theme: any): NavigationType[] => {
    const buttons: NavigationType[] = [];
    const { hasPreviousPage, hasNextPage, hideBackButton } = props;
    if (hasPreviousPage && !hideBackButton && theme.BackButtonVisible) {
        buttons.push(NavigationType.Back);
    }
    if (hasNextPage && theme.QuitButtonVisible) {
        buttons.push(NavigationType.Quit);
    }
    if (!hasNextPage && theme.SubmitButtonVisible) {
        buttons.push(NavigationType.Submit);
    } else {
        buttons.push(NavigationType.Next);
    }
    return buttons;
};

interface ShortcutProps {
    shortcut: ShortcutModel;
    onClick(): void;
}

const Shortcut = (props: ShortcutProps) => {
    const { onClick, shortcut } = props;

    const text = useTranslatable(shortcut, shortcut.blockName);
    return (
        <Button
            className="navigation__shortcut"
            text={text}
            key={`Shortcut_${shortcut.blockName}`}
            onClick={onClick}
            data-block={shortcut.blockName}
            size="flex"
        />
    );
};

const getButtonAlignment = (theme, position) => {
    return {
        Back: getAlignmentClass(
            theme.BackButtonPlacement,
            position,
            theme.BackButtonDisplayBothTopBottom,
        ),
        Submit: getAlignmentClass(
            theme.SubmitButtonPlacement,
            position,
            theme.SubmitButtonDisplayBothTopBottom,
        ),
        Next: getAlignmentClass(
            theme.NextButtonPlacement,
            position,
            theme.NextButtonDisplayBothTopBottom,
        ),
        Quit: getAlignmentClass(
            theme.QuitButtonPlacement,
            position,
            theme.QuitButtonDisplayBothTopBottom,
        ),
    };
};

const getAlignmentClass = (placement, position, displayBothTopBottom) => {
    if (placement.includes(position) || displayBothTopBottom) {
        if (placement.includes("left")) {
            return "navigationLeft";
        }
        if (placement.includes("right")) {
            return "navigationRight";
        }
        if (placement.includes("center")) {
            return "navigationCenter";
        }
    }
    return null;
};

const isPlacementOnPosition = (
    placement: string,
    navPosition: string,
    displayBothTopBottom?: boolean,
) => placement.includes(navPosition) || Boolean(displayBothTopBottom);

const isCenterPlacementOnPosition = (
    placement: string,
    navPosition: string,
    displayBothTopBottom?: boolean,
) =>
    isPlacementOnPosition(placement, navPosition, displayBothTopBottom) &&
    placement.includes("center");

type NavRowFilter = "all" | "primary" | "secondary";

const scrollIntoViewIfNeeded = (elementToFocus: HTMLElement) => {
    const rect = elementToFocus.getBoundingClientRect();
    const isInViewport =
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth);

    if (!isInViewport && typeof elementToFocus.scrollIntoView === "function") {
        elementToFocus.scrollIntoView({ behavior: "smooth", block: "start" });
    }
};

interface NavigationButtonProps {
    disabled?: boolean;
    surveyTheme: ThemeModel;
    types: NavigationType[];
    visibleTypes: NavigationType[];
    onClick(type: NavigationType);
    placeholderWhenHidden?: boolean;
    size: "flex" | "large";
}

export function NavigationButton(navButtonProps: NavigationButtonProps) {
    const {
        types,
        disabled,
        surveyTheme,
        onClick,
        visibleTypes,
        placeholderWhenHidden,
        size,
    } = navButtonProps;

    const theme = useTheme() as Theme;

    const type = types.find((t) => visibleTypes.includes(t)) || types[0];
    const hidden = !visibleTypes.includes(type);

    const useModernTheme = shouldUseModernTheme(theme);

    const dimension = useDimensionContext();
    const isSmallScreen = dimension && dimension.maxWidth(500);
    const themeButtonKey = `${NavigationType[type]}Button`;
    const displayType = theme[`${themeButtonKey}Type`] || "text";
    const icon = theme[`${themeButtonKey}Icon`] ? theme[`${themeButtonKey}Icon`].split(" ") : null;
    const iconOnHover = theme[`${themeButtonKey}IconOnHover`];
    const imageUrl = theme[`${themeButtonKey}ImageUrl`]
        ? theme[`${themeButtonKey}ImageUrl`].split(" ")
        : null;

    const showText = displayType === "text" || displayType === "textAndIcon";
    const showIcon = displayType === "icon" || displayType === "textAndIcon";

    const className = `navigation__${NavigationType[type].toLowerCase()}`;
    const text = useTranslatable(surveyTheme, `${NavigationType[type]}Button`);
    const buttonType = NavigationType[type].toUpperCase();

    if (hidden && !placeholderWhenHidden) return null;

    return (
        <Button
            className={className}
            text={text}
            displayText={!isSmallScreen && showText}
            icon={icon}
            displayIcon={showIcon || isSmallScreen}
            iconPosition={
                useModernTheme
                    ? type === NavigationType.Back || type === NavigationType.Quit
                        ? "start"
                        : "end"
                    : type === NavigationType.Back
                      ? "start"
                      : "end"
            }
            iconOnHover={iconOnHover}
            hidden={hidden}
            disabled={disabled}
            onClick={!hidden ? () => onClick(type) : undefined}
            size={size}
            imageUrl={imageUrl}
            buttonType={buttonType}
            outlineAccent={
                useModernTheme
                    ? type === NavigationType.Back
                        ? "secondary"
                        : type === NavigationType.Quit
                          ? "neutral"
                          : undefined
                    : undefined
            }
            fillAccent={
                useModernTheme && (type === NavigationType.Next || type === NavigationType.Submit)
                    ? "primary"
                    : undefined
            }
        />
    );
}

export default function NavigationBarRenderer(props: Props) {
    const {
        shortcuts,
        onNavigation,
        onNavigateShortcut,
        surveyTheme,
        disabled,
        position,
        showSecurityQuestion = false,
    } = props;

    const theme: any = useTheme();
    const classes = useStyles({ theme });
    const useModernTheme = shouldUseModernTheme(theme);
    const useModernBottomLayout = useModernTheme && position === "bottom";

    const block = useBlock();
    const displayShortcuts = block && getSettingBoolean(block, "DisplayShortcuts");

    const buttons = getButtons(props, theme);
    const buttonAlignments = getButtonAlignment(theme, position);
    const defaultFocusStatus = useSelector((state: StoreState) =>
        position === "bottom" ? state.defaultFocusStatus : null,
    );
    const navRef = React.useRef<HTMLElement>(null);

    const handleNavKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLElement>) => {
            if (position === "top") return;
            if (event.key !== "Tab" || !event.shiftKey) return;

            const nav = navRef.current;
            if (!nav) return;

            const focusableButtons = Array.from(
                nav.querySelectorAll<HTMLButtonElement>("button:not([disabled])"),
            );

            if (focusableButtons.length === 0) return;
            if (event.target !== focusableButtons[0]) return;

            const statuses = defaultFocusStatus?.statuses;
            if (!statuses || statuses.length === 0) return;

            let targetStatus = null;
            for (let i = statuses.length - 1; i >= 0; i--) {
                const status = statuses[i];
                if (status.elementRef?.current && isFocusable(status) && status.variableId !== 0) {
                    targetStatus = status;
                    break;
                }
            }

            if (!targetStatus?.elementRef?.current) return;

            const elementToFocus = targetStatus.elementRef.current;
            if (typeof elementToFocus.focus === "function") {
                try {
                    elementToFocus.focus({ preventScroll: true });
                } catch {
                    elementToFocus.focus();
                }
            }
            scrollIntoViewIfNeeded(elementToFocus);
            event.preventDefault();
        },
        [defaultFocusStatus, position],
    );

    const showViewResponsesAtBottom = shouldShowViewResponsesAtNavPosition(
        theme,
        position,
        showSecurityQuestion,
    );

    const { SubmitButtonPlacement: submitPosition } = theme;
    const buttonsSize = theme.BottomNavigationPosition === "floating" ? "flex" : "large";
    const navRowFilter: NavRowFilter = useModernBottomLayout ? "primary" : "all";

    const shouldRenderNavButton = (key: string, alignment: string, rowFilter: NavRowFilter) => {
        const themeButtonKey = `${key}Button`;
        const placement = theme[`${themeButtonKey}Placement`];
        const displayBoth = theme[`${themeButtonKey}DisplayBothTopBottom`];
        const alignmentClass = buttonAlignments[key];

        if (!useModernBottomLayout || rowFilter === "all") {
            return alignmentClass === alignment;
        }

        const onCenter = isCenterPlacementOnPosition(placement, position, displayBoth);
        if (rowFilter === "secondary") {
            return onCenter && alignment === "navigationCenter";
        }
        if (onCenter) {
            return false;
        }
        return alignmentClass === alignment;
    };

    const renderButtons = (alignment: string, rowFilter: NavRowFilter = "all") => {
        return ["Back", "Next", "Quit"].map((key) => {
            if (!shouldRenderNavButton(key, alignment, rowFilter)) {
                return null;
            }
            const navigationType = NavigationType[key as keyof typeof NavigationType];
            const buttonTypes =
                navigationType === NavigationType.Next
                    ? [NavigationType.Submit, NavigationType.Next]
                    : [navigationType];
            return (
                <NavigationButton
                    key={key}
                    placeholderWhenHidden={false}
                    disabled={disabled}
                    surveyTheme={surveyTheme}
                    visibleTypes={buttons}
                    onClick={onNavigation}
                    types={buttonTypes}
                    size={buttonsSize}
                />
            );
        });
    };

    const leftButton = renderButtons("navigationLeft", navRowFilter);
    const centerButton = renderButtons("navigationCenter", navRowFilter);
    const rightButton = renderButtons("navigationRight", navRowFilter);
    const centerSecondaryButtons = useModernBottomLayout
        ? renderButtons("navigationCenter", "secondary")
        : [];
    const showCenterRow =
        useModernBottomLayout &&
        (showViewResponsesAtBottom || centerSecondaryButtons.some((button) => button));

    const isRenderPrimary = [leftButton, centerButton, rightButton].some((buttonGroup) =>
        buttonGroup.some((button) => button),
    );

    if (buttons.length === 0 && !showCenterRow) return null;
    const isRenderShortCuts = displayShortcuts && submitPosition.includes(position);

    return (
        <>
            {isRenderPrimary || showCenterRow || isRenderShortCuts ? (
                <nav ref={navRef} className={classes.navigation} onKeyDown={handleNavKeyDown}>
                    {isRenderPrimary && (
                        <div className={classes.navigationButtonContainer}>
                            <div className={classes.navigationLeft}>{leftButton}</div>
                            <div className={classes.navigationCenter}>{centerButton}</div>
                            <div className={classes.navigationRight}>{rightButton}</div>
                        </div>
                    )}
                    {showCenterRow && (
                        <div className={classes.navigationSecondaryRow}>
                            {showViewResponsesAtBottom && (
                                <ViewResponsesButton
                                    surveyTheme={surveyTheme}
                                    size={buttonsSize}
                                />
                            )}
                            {centerSecondaryButtons}
                        </div>
                    )}
                    {isRenderShortCuts && (
                        <div className={classes.navigationButtonContainer}>
                            {shortcuts.map((shortcut) => (
                                <Shortcut
                                    key={shortcut.blockName}
                                    onClick={() => onNavigateShortcut(shortcut)}
                                    shortcut={shortcut}
                                />
                            ))}
                        </div>
                    )}
                </nav>
            ) : (
                <div className={classes.navigationPlaceHolder} />
            )}
        </>
    );
}
